import useSWR from "swr";
import axios, { AxiosError } from "axios";
import { useContext } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { ADMIN_API_URL } from "../../../utils/env";

// One nested per-account row inside IStrategyOverview.accounts. An account
// surfaces here if it appears in ANY of the strategy's state buckets
// (settled shares, pending/committed deposits or withdrawals, or an
// in-process deposit/withdrawal), so brand-new depositors show up even
// before shares are credited on rollover. Amounts are decimal strings
// (num.Num.Text()) — render them directly, no 1e6 scaling.
export interface IStrategyAccountDetail {
  account: string;
  shares: string;
  deposits: string;
  withdrawals: string;
  average_pps: string;
  pending_deposit: string;
  pending_withdrawal: string;
  commited_deposit: string;
  commited_withdrawal: string;
  in_process_deposit_count: number;
  in_process_withdrawal_count: number;
}

// One row per registered strategy from GET /strategies. Top-level counters
// give an at-a-glance picture; `accounts` holds the per-account breakdown
// for drill-down.
export interface IStrategyOverview {
  address: string;
  name: string;
  state: string;
  cap: string;
  target_utilization: string;
  pps: string;
  deposit_collateral: string;
  withdrawal_collateral: string;
  total_deposits: string;
  total_withdrawals: string;
  account_count: number;
  pending_deposits_count: number;
  pending_withdrawals_count: number;
  in_process_deposits_count: number;
  in_process_withdrawals_count: number;
  commited_deposits_count: number;
  commited_withdrawals_count: number;
  accounts: IStrategyAccountDetail[];
}

// GET /strategies on the exchange-backend admin service (READ_SYSTEM).
// Returns every AEVO strategy with its lifecycle state and per-account
// pending/committed/in-process deposit & withdrawal breakdown. Polled every
// 30s so the page reflects the initiate → pending → commit → rollover
// lifecycle without a manual reload.
export const useStrategies = () => {
  const { queryConfig, tokenId } = useContext(AuthContext);

  const endpoint = ADMIN_API_URL(AdminEndpointEnum.STRATEGIES);

  return useSWR<IStrategyOverview[], AxiosError>(
    tokenId ? [endpoint, tokenId] : null,
    async () => {
      const response = await axios.get<IStrategyOverview[]>(
        endpoint,
        queryConfig()
      );
      return response.data;
    },
    { refreshInterval: 30000 }
  );
};
